import { STATUS } from "@/constants/statuses";
import { applyTransaction } from "@datorama/akita";
import { JsonMimeType } from "@lib/ApiClient";
import ApiClientInterface from "@lib/ApiClient/ApiClientInterface";
import { Observable } from "rxjs";
import { catchError, map, tap } from "rxjs/operators";
import { create } from "superstruct";
import { inject, singleton } from "tsyringe";

import { LoggerInterface } from "@interfaces/LoggerInterface";

import VoteEntity from "../entities/VoteEntity";
import VoteSchema from "../schemas/VoteSchema";
import VotesStore from "../stores/VotesStore";
import {
    reviewsApplicationLoggerToken,
    reviewsAuthApiClientToken,
} from "../tokens";

@singleton()
class VotesService {
    constructor(
        protected votesStore: VotesStore,
        @inject(reviewsAuthApiClientToken)
        protected readonly authApiClient: ApiClientInterface,
        @inject(reviewsApplicationLoggerToken)
        protected readonly logger: LoggerInterface
    ) {}

    toggleVote(reviewId: number): Observable<VoteEntity> {
        this.votesStore.update(reviewId, (state) => {
            return {
                votes: state.isCustomerVoted
                    ? state.votes - 1
                    : state.votes + 1,
                isCustomerVoted: !state.isCustomerVoted,
                status: STATUS.LOADING,
            };
        });

        return this.authApiClient
            .post({
                url: "/review/toggleVote",
                body: JSON.stringify({ reviewId }),
                responseType: "json",
                contentType: JsonMimeType,
            })
            .pipe(
                map((res) => create(res.body, VoteSchema)),
                catchError((error: unknown) => {
                    this.logger.debug("VotesService.toggleVote is errored");
                    applyTransaction(() => {
                        this.votesStore.update(reviewId, {
                            status: STATUS.ERROR,
                        });
                        this.votesStore.setError(error as Error);
                    });
                    throw error;
                }),
                tap((data) => {
                    this.logger.debug("VotesService.toggleVote is ready");
                    this.votesStore.update(reviewId, {
                        votes: data.votes,
                        isCustomerVoted: data.isCustomerVoted,
                        status: STATUS.READY,
                    });
                })
            );
    }
}

export default VotesService;
